<template>
  <div v-if="!isProgramsPendingRef" class="flex h-dvh w-full overflow-hidden">
    <a href="#main-content" class="skip-link" @click.prevent="skipToContent">
      {{ t('ui.layout.skipToContent') }}
    </a>
    <SidebarProvider>
      <MainNav />
      <SidebarInset class="flex flex-col overflow-hidden">
        <!-- page de chat tous programmes : pas de barre du haut ni de selecteur de programme -->
        <TopNav v-if="!isChatRouteRef" />
        <div
          id="main-content"
          tabindex="-1"
          :class="[
            'flex-1 overflow-y-auto outline-none',
            isChatRouteRef ? 'bg-background p-0' : 'bg-[#FBFBFB] p-4 md:p-6',
          ]"
        >
          <MobileNav />
          <slot name="middle"></slot>
        </div>
      </SidebarInset>
    </SidebarProvider>
    <slot name="outside"></slot>
    <AppHooks />
    <NameModal />
    <PhoneModal />
    <AddToCalendarModal />
    <ProgramObjectiveModal />
    <NoProgramModal />
    <MentorMatchingModal />
    <MenteeMatchingModal />
    <NewMatchingModal />
    <NoMentorFoundModal />
    <GroupSettingsModal />
    <ObjectiveDescriptionModal />
    <LinkSessionToObjectiveModal />

    <NewSessionModal />
    <EditSessionModal />
    <SessionDetailsModal />
    <ReviewSessionModal />

    <UserProfileModal />
    <EditUserModal />
    <ImpersonationReasonModal />

    <PeriodsPopover />
    <GroupsPopover />
    <MoreActionsMenu />
    <ConfirmActionModal />
    <ContactWarningModal />

    <!-- Should be last modal so that it is opened on top of all other modals -->
    <TermsModal />
  </div>
</template>

<script setup>
import { computed } from 'vue'

import AppHooks from './app-hooks.vue'

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import ImpersonationReasonModal from '@/features/admin/users/components/impersonation-reason-modal/impersonation-reason-modal.vue'
import NameModal from '@/features/auth/components/name-modal/name-modal.vue'
import GroupSettingsModal from '@/features/groups/components/group-settings-modal/group-settings-modal.vue'
import GroupsPopover from '@/features/groups/components/groups-popover/groups-popover.vue'
import TermsModal from '@/features/legal/components/terms-modal/terms-modal.vue'
import MenteeMatchingModal from '@/features/matching/components/matching-modals/mentee-matching-modal/mentee-matching-modal.vue'
import MentorMatchingModal from '@/features/matching/components/matching-modals/mentor-matching-modal/mentor-matching-modal.vue'
import NoMentorFoundModal from '@/features/matching/components/matching-modals/no-mentor-found-modal/no-mentor-found-modal.vue'
import NewMatchingModal from '@/features/matching/components/new-matching-modal/new-matching-modal.vue'
import LinkSessionToObjectiveModal from '@/features/programs/components/link-session-to-objective-modal/link-session-to-objective-modal.vue'
import NoProgramModal from '@/features/programs/components/no-program-modal/no-program-modal.vue'
import ObjectiveDescriptionModal from '@/features/programs/components/objective-description-modal/objective-description-modal.vue'
import PeriodsPopover from '@/features/programs/components/periods-popover/periods-popover.vue'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import AddToCalendarModal from '@/features/sessions/components/add-to-calendar-modal/add-to-calendar-modal.vue'
import ContactWarningModal from '@/features/sessions/components/contact-warning-modal/contact-warning-modal.vue'
import EditSessionModal from '@/features/sessions/components/edit-session-modal/edit-session-modal.vue'
import NewSessionModal from '@/features/sessions/components/new-session-modal/new-session-modal.vue'
import ProgramObjectiveModal from '@/features/sessions/components/program-objective-modal/program-objective-modal.vue'
import ReviewSessionModal from '@/features/sessions/components/review-session-modal/review-session-modal.vue'
import SessionDetailsModal from '@/features/sessions/components/session-details-modal/session-details-modal.vue'
import ConfirmActionModal from '@/features/ui/components/confirm-action-modal/confirm-action-modal.vue'
import MainNav from '@/features/ui/components/layout/main-nav/main-nav.vue'
import MobileNav from '@/features/ui/components/layout/mobile-menu/mobile-menu.vue'
import PhoneModal from '@/features/ui/components/layout/phone-modal/phone-modal.vue'
import TopNav from '@/features/ui/components/layout/top-nav/top-nav.vue'
import MoreActionsMenu from '@/features/ui/components/more-actions-menu/more-actions-menu.vue'
import EditUserModal from '@/features/ui/pages/admin/users/edit-user-modal/edit-user-modal.vue'
import UserProfileModal from '@/features/users/components/user-profile-modal/user-profile-modal.vue'
import { useI18n } from '@/lib/i18n'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'
import { ROUTES } from '@/utils/config/constants'

const { t } = useI18n()
const { isPendingRef: isProgramsPendingRef } = usePrograms()
const currentRouteRef = selectGetCurrentRoute(routeStore)
const isChatRouteRef = computed(() => !!currentRouteRef.value?.path?.startsWith(ROUTES.CHAT()))

function skipToContent() {
  const el = document.getElementById('main-content')
  if (el) {
    el.focus()
    el.scrollIntoView()
  }
}
</script>

<style scoped>
.skip-link {
  position: absolute;
  left: -9999px;
  z-index: 999;
  padding: 0.5rem 1rem;
  background: var(--background);
  color: var(--foreground);
  border: 2px solid var(--foreground);
  border-radius: 0.25rem;
  font-size: 0.875rem;
  text-decoration: none;
}

.skip-link:focus {
  left: 0.5rem;
  top: 0.5rem;
}
</style>
